'use client'

import { deepmerge } from 'deepmerge-ts'
import { DeepPartial } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { ZUSTAND_APP_STORE_LOCAL_STORAGE_KEY, ZUSTAND_APP_STORE_LOCAL_STORAGE_VERSION } from '@/config/consts'
import { filterOutUndefinedKeys } from '@/utils/object'
import { Serializable } from '@/utils/types'

import {
  ActionsSettings,
  ActionsSettingsSlice,
  PersistedActionsSettingsSlice,
  initActionsSettingsSlice,
  persistActionsSettingsSlice,
  unPersistActionsSettingsSlice,
} from './actions-settings'
import {
  AnalyticsConsentSlice,
  PersistedAnalyticsConsentSlice,
  initAnalyticsConsentSlice,
  persistAnalyticsConsentSlice,
} from './analyticsConsent'
import {
  BannersVisibilitySlice,
  initBannersVisibilitySlice,
  persistBannersVisibilitySlice,
  PersistedBannersSlice,
} from './bannersVisibility'
import { ComplianceSlice, PersistedComplianceSlice, initComplianceSlice, persistComplianceSlice } from './compliance'
import { DialogSlice } from './dialogs'
import { initDialogSlice } from './dialogSlice'
import { InkeepSlice, initInkeepSlice } from './inkeep'
import { SideDrawerSlice, initSideDrawerSlice, PersistedSideDrawerSlice, persistSideDrawerSlice } from './sideDrawer'
import {
  CardAuthSlice,
  initCardAuthSlice,
  PersistedCardAuthSlice,
  persistCardAuthSlice,
  restorePersistedCardAuthSlice,
} from './cardAuth'
import {
  ViewPreferences,
  ViewPreferencesSlice,
  PersistedViewPreferencesSlice,
  initViewPreferencesSlice,
  persistViewPreferencesSlice,
} from './viewPreferences'
import {
  UserPreferences,
  UserPreferencesSlice,
  PersistedUserPreferencesSlice,
  initUserPreferencesSlice,
  persistUserPreferencesSlice,
} from './userPreferences'
import {
  PositionNames,
  PositionNamesSlice,
  PersistedPositionNamesSlice,
  initPositionNamesSlice,
  persistPositionNamesSlice,
} from './positionNames'
import {
  CardSolverConfigs,
  CardSolverConfigsSlice,
  PersistedCardSolverConfigsSlice,
  initCardSolverConfigsSlice,
  persistCardSolverConfigsSlice,
} from './cardSolverConfigs'

export type StoreState = DialogSlice &
  ActionsSettingsSlice &
  AnalyticsConsentSlice &
  ComplianceSlice &
  BannersVisibilitySlice &
  InkeepSlice &
  SideDrawerSlice &
  CardAuthSlice &
  CardSolverConfigsSlice &
  ViewPreferencesSlice &
  UserPreferencesSlice &
  PositionNamesSlice

export type PersistedState = Serializable<
  PersistedActionsSettingsSlice &
    PersistedAnalyticsConsentSlice &
    PersistedComplianceSlice &
    PersistedSideDrawerSlice &
    PersistedBannersSlice &
    PersistedCardSolverConfigsSlice &
    PersistedViewPreferencesSlice &
    PersistedUserPreferencesSlice &
    PersistedPositionNamesSlice
> &
  PersistedCardAuthSlice

export const storeImplementation = persist<StoreState, [], [], PersistedState>(
  function initializer(...a): StoreState {
    return {
      ...initDialogSlice(...a),
      ...initActionsSettingsSlice(...a),
      ...initAnalyticsConsentSlice(...a),
      ...initComplianceSlice(...a),
      ...initBannersVisibilitySlice(...a),
      ...initInkeepSlice(...a),
      ...initSideDrawerSlice(...a),
      ...initCardAuthSlice(...a),
      ...initCardSolverConfigsSlice(...a),
      ...initViewPreferencesSlice(...a),
      ...initUserPreferencesSlice(...a),
      ...initPositionNamesSlice(...a),
    }
  },
  {
    name: ZUSTAND_APP_STORE_LOCAL_STORAGE_KEY,
    version: ZUSTAND_APP_STORE_LOCAL_STORAGE_VERSION,
    partialize: (state): PersistedState => ({
      ...persistActionsSettingsSlice(state),
      ...persistAnalyticsConsentSlice(state),
      ...persistComplianceSlice(state),
      ...persistBannersVisibilitySlice(state),
      ...persistSideDrawerSlice(state),
      ...persistCardAuthSlice(state),
      ...persistCardSolverConfigsSlice(state),
      ...persistViewPreferencesSlice(state),
      ...persistUserPreferencesSlice(state),
      ...persistPositionNamesSlice(state),
    }),
    merge: (_persistedState, currentState) => {
      const persistedState = (_persistedState ?? {}) as DeepPartial<PersistedState>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { actionsSettings, cardAuth, ...rest } = persistedState

      const processedPersistedState = filterOutUndefinedKeys({
        ...rest,
        actionsSettings: unPersistActionsSettingsSlice(persistedState).actionsSettings,
        cardAuth: cardAuth
          ? {
              ...currentState.cardAuth,
              ...restorePersistedCardAuthSlice(cardAuth),
            }
          : currentState.cardAuth,
      })

      return deepmerge(currentState, processedPersistedState) as StoreState
    },
  },
)

export const useStore = create<StoreState>()(storeImplementation)

export function useActionsSettings(): ActionsSettings {
  return useStore((state) => state.actionsSettings)
}

export function useCardAuth() {
  return useStore((state) => state.cardAuth)
}

export function useCardSolverConfigs(): CardSolverConfigs {
  return useStore((state) => state.cardSolverConfigs)
}

export function useStoreHydrated(): boolean {
  const [hydrated, setHydrated] = useState(useStore.persist.hasHydrated())

  useEffect(() => {
    if (useStore.persist.hasHydrated()) {
      setHydrated(true)
      return
    }

    return useStore.persist.onFinishHydration(() => {
      setHydrated(true)
    })
  }, [])

  return hydrated
}

export function useViewPreferences(): ViewPreferences {
  return useStore((state) => state.viewPreferences)
}

export function useUserPreferences(): UserPreferences {
  return useStore((state) => state.userPreferences)
}

export function usePositionNames(): PositionNames {
  return useStore((state) => state.positionNames)
}
